import { ArrowLeft, Mail, PhoneCall } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'
import { useAuthenticationState } from '~/atoms/AuthenticationState'
import { useUserState } from '~/atoms/UserState'
import { Text } from '~/components/core'
import { FontStyle } from '~/components/core/Text'
import { Loading } from '~/components/Loading'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { PbxPicker } from '~/components/shared/PbxPicker'
import { TextButton } from '~/components/shared/TextButton'
import { MANGO_SUPPORT_PHONE } from '~/constants/support'
import { useAnalytics } from '~/hooks/useAnalytics'
import { usePartnerLogo } from '~/hooks/useCurrentPartner'
import {
  ButtonWrapper,
  EmptyWrapper,
  HelpWrapper,
  LogoImage,
  PbxWrapper,
  TitleWrapper,
  Wrapper,
} from '~/pages/Login/Login.styles'
import { useGetPbxList } from '~/queries/pbxQueries'
import {
  AnalyticsNamespace,
  AuthenticationAnalyticsEvents,
} from '~/types/Analytics'
import { openInNewTab } from '~/utils/functions'
import { addRecentPbx } from '~/utils/recentPbxs'

import { Pbx } from '@mangovoice/types'

type PbxSummary = Pick<Pbx, 'id' | 'name'>

const BackIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <ArrowLeft {...props} />
)

const p1TextStyles = {
  marginTop: '20px',
  marginBottom: '20px',
  fontWeight: '400',
  fontSize: '16px',
}

const p2TextStyles = {
  marginBottom: '20px',
  fontWeight: '400',
  fontSize: '16px',
}

interface ExtensionPromptProps {
  onBackButtonPressed?: () => void
}
export const PbxPrompt = ({ onBackButtonPressed }: ExtensionPromptProps) => {
  const { t } = useTranslation()
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [selectedPbx, setSelectedPbx] = useState<PbxSummary>()
  const { jwtData } = useAuthenticationState()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [hasNoPbx, setHasNoPbx] = useState(false)
  const { authenticationState } = useAuthenticationState()
  const { track } = useAnalytics(AnalyticsNamespace.AUTHENTICATION)
  const { isErrored: isUserStateErrored, getUserSessionDependencies } =
    useUserState()
  const partnerLogo = usePartnerLogo()

  const { data: pbxList, isLoading: pbxListLoading } = useGetPbxList({
    apiParams: { limit: 100 },
  })

  useEffect(() => {
    if (!authenticationState.appSessionJwts || !selectedPbx) {
      return
    }

    setLoading(true)

    // getUserSessionDependencies expects full Pbx but only reads .id
    getUserSessionDependencies(selectedPbx as Pbx)
  }, [authenticationState.session, selectedPbx])

  useEffect(() => {
    if (loading && isUserStateErrored) {
      setErrorMsg(t('pbxPrompt.errorMessage'))
      setSelectedPbx(undefined)
      setLoading(false)
    }
  }, [loading, isUserStateErrored])

  const handlePbxSelected = (pbx: PbxSummary) => {
    if (!jwtData?.access_token) {
      setErrorMsg(t('pbxPrompt.errorMessage'))
      return
    }
    track(AuthenticationAnalyticsEvents.PbxSelected)
    addRecentPbx(pbx)
    setErrorMsg(null)
    setSelectedPbx(pbx)
    const returnUrl = searchParams.get('returnUrl') || '/'
    navigate(returnUrl)
  }

  const handleBackButtonPressed = () => {
    track(AuthenticationAnalyticsEvents.ExtensionBackButtonPressed)
    onBackButtonPressed?.()
  }

  const handleCallSupport = () => {
    track(AuthenticationAnalyticsEvents.CallSupportClicked)
    openInNewTab(`tel:${MANGO_SUPPORT_PHONE.digits}`)
  }

  const handleEmailSupport = () => {
    track(AuthenticationAnalyticsEvents.EmailSupportClicked)
    openInNewTab('mailto:support@mangovoice.com')
  }

  useEffect(() => {
    if (!jwtData?.access_token) {
      return
    }
    if (!pbxListLoading && pbxList?.count === 1) {
      handlePbxSelected(pbxList.results[0])
    } else if (!pbxListLoading && pbxList?.count === 0) {
      setHasNoPbx(true)
    }
  }, [pbxList?.count ?? 0, pbxListLoading, jwtData?.access_token])

  return (
    <div>
      {selectedPbx ? (
        <>
          <Wrapper>
            <LogoImage alt="logo" src={partnerLogo} />
            <TitleWrapper>
              <Text
                fontStyle={FontStyle.Medium}
                text={t('pbxPrompt.loggingIn')}
                weight="normal"
                color="var(--chakra-colors-fg-subtle)"
              />
              <Loading />
            </TitleWrapper>
          </Wrapper>
        </>
      ) : pbxListLoading ? (
        <>
          <Wrapper>
            <LogoImage alt="logo" src={partnerLogo} />
            <TitleWrapper>
              <Text
                fontStyle={FontStyle.Medium}
                text={t('pbxPrompt.loadingPbxs')}
                weight="normal"
                color="var(--chakra-colors-fg-subtle)"
              />
              <Loading />
            </TitleWrapper>
          </Wrapper>
        </>
      ) : hasNoPbx ? (
        <EmptyWrapper>
          <LogoImage alt="logo" src={partnerLogo} />
          <Text style={p1TextStyles}>{t('pbxPrompt.noPbxMessage')}</Text>
          <Text style={p2TextStyles}>{t('pbxPrompt.noPbxErrorMessage')}</Text>
          <HelpWrapper>
            <TextButton leftIcon={PhoneCall} onLinkClick={handleCallSupport}>
              {t('pbxPrompt.callSupport')} {MANGO_SUPPORT_PHONE.formatted}
            </TextButton>
            <TextButton leftIcon={Mail} onLinkClick={handleEmailSupport}>
              {t('pbxPrompt.emailSupport')}
            </TextButton>
          </HelpWrapper>
          <ButtonWrapper>
            <MangoButton
              leftIcon={BackIcon}
              leftIconProps={{ width: '12px', height: '12px' }}
              variant={MangoButtonVariant.Default_200}
              onClick={handleBackButtonPressed}
            >
              {t('pbxPrompt.back')}
            </MangoButton>
          </ButtonWrapper>
        </EmptyWrapper>
      ) : (
        <>
          <Wrapper>
            <LogoImage alt="logo" src={partnerLogo} />
            <TitleWrapper>
              <Text
                fontStyle={FontStyle.Medium}
                text={t('pbxPrompt.selectPbx')}
                weight="bold"
              />
            </TitleWrapper>
            <PbxWrapper>
              <PbxPicker
                onSelect={handlePbxSelected}
                initialPbxList={pbxList}
                isLoading={pbxListLoading}
              />
            </PbxWrapper>
            {errorMsg && (
              <>
                <Text
                  color="var(--chakra-colors-red-solid)"
                  fontStyle={FontStyle.bSmall}
                  text={t('pbxPrompt.errorTitle')}
                  align="center"
                />
                <Text
                  color="var(--chakra-colors-red-solid)"
                  fontStyle={FontStyle.bSmall}
                  text={errorMsg}
                  align="center"
                />
              </>
            )}
            <ButtonWrapper>
              <MangoButton
                leftIcon={BackIcon}
                leftIconProps={{ width: '12px', height: '12px' }}
                variant={MangoButtonVariant.Default_200}
                onClick={handleBackButtonPressed}
              >
                {t('pbxPrompt.back')}
              </MangoButton>
            </ButtonWrapper>
          </Wrapper>
        </>
      )}
    </div>
  )
}
